import { useEffect, useRef, MutableRefObject } from 'react';
import classNames from 'classnames';
import useBreakpoints from 'src/hooks/useBreakpoints';
import cityStyles from 'src/styles/modules/pages/city.module.scss';
import { Modal } from 'react-bootstrap';
import ClientOnlyPortal from './ClientOnlyPortal';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { FiX } from 'react-icons/fi';

type Props = {
  title: string;
  isShow: boolean;
  isModal: boolean;
  onClose: () => void;
};

const ModalOrDiv: React.ReactFCWithChildren<{
  isModal: boolean;
  show: boolean;
  sidebarRef?: MutableRefObject<HTMLDivElement | null>;
  onClose: () => void;
}> = ({ isModal, show, sidebarRef, children, onClose }) => {
  return isModal ? (
    <Modal
      show={show}
      onHide={() => {
        onClose();
      }}
      dialogClassName="modal-dialog-bottom modal-almost-full-height--sp modal-dialog-scrollable modal-lg"
      contentClassName="tw-rounded-tl-2xl tw-rounded-tr-2xl"
    >
      <Modal.Body className="tw-py-0 tw-px-5">{children}</Modal.Body>
    </Modal>
  ) : (
    <div
      className={classNames(
        `tw-fixed tw-right-0 tw-w-screen md:tw-max-w-xl tw-z-1200 ${cityStyles.cityPopup}`,
        {
          'tw-hidden': !show,
        }
      )}
    >
      <div
        ref={sidebarRef}
        className="tw-bg-brand-yellow tw-px-10 tw-pb-6 tw-overflow-y-auto tw-overflow-x-hidden custom-scrollbar tw-h-full"
      >
        {children}
      </div>
    </div>
  );
};

const ContentListSidebar: React.ReactFCWithChildren<Props> = ({
  title,
  isModal,
  isShow,
  onClose,
  children,
}) => {
  const { isScreenMd } = useBreakpoints();
  const sidebarRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (sidebarRef.current) {
      if (isShow) {
        disableBodyScroll(sidebarRef.current);
      } else {
        enableBodyScroll(sidebarRef.current);
      }
    }
  }, [isShow]);

  return (
    <>
      <ModalOrDiv
        isModal={isModal}
        show={isShow}
        sidebarRef={sidebarRef}
        onClose={() => {
          onClose();
        }}
      >
        <div className="tw-sticky tw-top-0 tw-z-20 tw-bg-brand-yellow md:tw-pt-8 tw-pt-5">
          <div className="tw-flex tw-items-center tw-justify-between">
            <div className="tw-font-styleSans tw-text-xl tw-font-semibold tw-leading-none md:tw-text-2xl md:tw-leading-none">
              {title}
            </div>

            <button
              type="button"
              aria-label="Close"
              className="tw-border-0 tw-bg-transparent tw-p-0 tw-text-right"
              onClick={() => {
                onClose();
              }}
            >
              <FiX className="tw-h-5 tw-w-5 tw-text-brand-navy md:tw-h-6 md:tw-w-6" />
            </button>
          </div>

          <div className="tw-my-4 tw-border-0 tw-border-t tw-border-solid tw-border-grey-lighter" />
        </div>

        {children}
      </ModalOrDiv>

      <ClientOnlyPortal selector="#bottom-body">
        {isScreenMd ? (
          <div
            onClick={() => {
              onClose();
            }}
            className={classNames(
              'tw-bg-black tw-bottom-0 tw-left-0 tw-opacity-30 tw-fixed tw-w-screen tw-z-101 dropdown-mask-height',
              {
                'tw-hidden': !isShow,
              }
            )}
          ></div>
        ) : null}
      </ClientOnlyPortal>
    </>
  );
};

export default ContentListSidebar;
